import { apiClient } from './client';

export interface RegistrationResponse {
  id: string;
  status: string;
  message: string;
  auth?: {
    accessToken: string;
    refreshToken: string;
  };
}

export interface RegistrationStatus {
  id: string;
  academy_name: string;
  academy_slug: string;
  mobile: string;
  address: string;
  status: string;
  otp_verified_at: string | null;
  reviewed_at: string | null;
  admin_notes: string | null;
  academy_id: string | null;
  created_at: string;
}

export interface RegistrationSummary {
  id: string;
  user_id: string;
  display_name: string;
  email: string;
  academy_name: string;
  academy_slug: string;
  mobile: string;
  address: string;
  status: string;
  otp_verified_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  admin_notes: string | null;
  academy_id: string | null;
  created_at: string;
}

const API_BASE = '/assess/api';

export async function submitRegistration(data: {
  display_name: string;
  email: string;
  password: string;
  academy_name: string;
  mobile: string;
  address: string;
}): Promise<RegistrationResponse> {
  const res = await fetch(`${API_BASE}/academy-register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Registration failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function verifyOtp(registrationId: string, otpCode: string): Promise<{ success: boolean; status: string; message: string }> {
  const res = await fetch(`${API_BASE}/academy-register/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ registration_id: registrationId, otp_code: otpCode }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Verification failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function resendOtp(registrationId: string): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`${API_BASE}/academy-register/resend-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ registration_id: registrationId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Resend failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function getRegistrationStatus(): Promise<RegistrationStatus> {
  return apiClient.get('/academy-register/status');
}

export interface PublicRegistrationStatus {
  academy_name: string;
  status: string;
  reviewed_at: string | null;
  admin_notes: string | null;
  created_at: string;
}

export async function checkRegistrationStatusByEmail(
  email: string,
): Promise<PublicRegistrationStatus> {
  const res = await fetch(`${API_BASE}/academy-register/check-status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Status check failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function listRegistrations(status?: string): Promise<{ registrations: RegistrationSummary[] }> {
  const qs = status ? `?status=${status}` : '';
  return apiClient.get(`/admin/academy-registrations${qs}`);
}

export async function reviewRegistration(
  id: string,
  action: 'approved' | 'rejected' | 'info_requested',
  notes?: string,
): Promise<{ success: boolean; status: string }> {
  return apiClient.put(`/admin/academy-registrations/${id}/review`, { action, notes });
}
